// Paddle Service for frontend
// Price IDs: keep mapPlanIdToPriceId in sync with llm_server/deployment_package/plan_slug_catalog.py
import apiService from './apiService';
import { getAuthUser } from '../utils/authTokenStore';
import { getConfig } from '../config/appConfig';
import { storeCheckoutContext, clearStoredCheckoutContext } from '../utils/checkoutReturnParams';

class PaddleService {
  constructor() {
    this.paddle = null;
    this.clientKey = null;
  }

  // Wait for Paddle.js to be fully ready (returns a Promise)
  async waitForPaddleReady() {
    return new Promise((resolve, reject) => {
      // If already ready, resolve immediately
      if (typeof window !== 'undefined' && window.Paddle && window.Paddle.Checkout) {
        resolve(window.Paddle);
        return;
      }

      // Check if script is loading
      const checkInterval = setInterval(() => {
        if (typeof window !== 'undefined' && window.Paddle && window.Paddle.Checkout) {
          clearInterval(checkInterval);
          resolve(window.Paddle);
        }
      }, 50); // Check every 50ms

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        reject(new Error('Paddle.js failed to load within 10 seconds'));
      }, 10000);
    });
  }

  // Initialize Paddle.js
  async initializePaddle() {
    if (this.paddle) {
      // Ensure Paddle.js is fully ready
      await this.waitForPaddleReady();
      return this.paddle;
    }

    try {
      // Load Paddle.js script if not already loaded
      if (typeof window !== 'undefined' && typeof window.Paddle === 'undefined') {
        await this.loadPaddleScript();
        // Wait for Paddle.js to be fully initialized after script loads
        await this.waitForPaddleReady();
      } else {
        // Script already loaded, just wait for it to be ready
        await this.waitForPaddleReady();
      }

      if (typeof window !== 'undefined' && window.Paddle) {
        // Determine environment before setup
        let clientKey = getConfig('paddle.clientKey');
        
        // Helper to check if key is empty or a placeholder
        const isPlaceholder = (val) => !val || val === 'YOUR_LIVE_CLIENT_KEY_HERE' || String(val).startsWith('YOUR_');
        
        if (isPlaceholder(clientKey)) {
          clientKey = await this.getClientKeyFromAPI();
        }

        // ***** Code Start BH28072026_1045 - Fail-closed Paddle key (no sandbox fallback in production)
        const { resolvePaddleClientKey, resolvePaddleEnvironment } = await import('../config/paddleConfig');
        clientKey = resolvePaddleClientKey(clientKey);
        const resolvedEnvironment = resolvePaddleEnvironment(clientKey);
        // ***** Code End BH28072026_1045

        if (typeof window.Paddle.Environment?.set === 'function') {
          try {
            window.Paddle.Environment.set(resolvedEnvironment);
            // Environment set successfully
          } catch (envError) {
            // Failed to set environment
          }
        }

        if (clientKey) {
          this.clientKey = clientKey;
          // Paddle.js v2 honours one Initialize — this is the app's sole initializer,
          // and the only place checkout.completed / checkout.closed are registered.
          // PaddleContext delegates here; nothing else may call Paddle.Initialize.
          if (typeof window.Paddle.Initialize === 'function' && !window.__candorPaddleInitialized) {
            // ***** Code Start BH30072026_1245 - Read the buyer email when the event fires
            // Initialization now happens at app mount, which can be before sign-in, so
            // reading candor_user here would capture null for the whole session.
            const readUserEmail = () => {
              try {
                const candorUserObj = getAuthUser();
                const candorUser = candorUserObj ? JSON.stringify(candorUserObj) : null;
                return candorUser ? JSON.parse(candorUser).email : null;
              } catch (_) {
                return null;
              }
            };
            // ***** Code End BH30072026_1245
            window.Paddle.Initialize({
              token: clientKey,
              eventCallback: (eventData) => {
                const eventName = eventData?.event || eventData?.name;

                // ***** Code Start BH29072026_1830 - Abandoning the overlay must leave nothing to verify
                // The checkout context is stored before the overlay opens so it survives a
                // redirect. If the buyer closes the overlay instead of paying, drop it so no
                // later mount can pick the draft transaction up and verify it.
                // ***** Code Start BH30072026_1515 - Keep context after a paid completion
                // Paddle often emits checkout.closed after checkout.completed (or during the
                // successUrl navigation). Clearing here wiped the che_/txn_ that SuccessPage
                // needs when the redirect URL has no transaction_id.
                if (eventName === 'checkout.closed') {
                  try {
                    if (sessionStorage.getItem('checkout_paid') === '1') {
                      return;
                    }
                  } catch (_) {
                    /* ignore */
                  }
                  clearStoredCheckoutContext();
                  return;
                }
                // ***** Code End BH30072026_1515
                // ***** Code End BH29072026_1830

                if (eventName === 'checkout.completed') {
                  // ***** Code Start BH30072026_1515 - Survive sparse Paddle event payloads
                  const txn = eventData.data || {};
                  const storedId = (() => {
                    try {
                      return sessionStorage.getItem('checkout_session_id');
                    } catch (_) {
                      return null;
                    }
                  })();
                  const txnId =
                    txn.id ||
                    txn.transaction_id ||
                    txn.transaction?.id ||
                    txn.checkout?.id ||
                    storedId;
                  const customData = txn.custom_data || {};
                  let planId = customData.plan_id;
                  let cycle = customData.billing_cycle;
                  try {
                    planId = planId || sessionStorage.getItem('checkout_plan_id') || 'pro_plan';
                    cycle =
                      cycle ||
                      sessionStorage.getItem('checkout_billing_cycle') ||
                      'monthly';
                    sessionStorage.setItem('checkout_paid', '1');
                  } catch (_) {
                    planId = planId || 'pro_plan';
                    cycle = cycle || 'monthly';
                  }
                  if (txnId) {
                    storeCheckoutContext({
                      sessionId: txnId,
                      planId,
                      billingCycle: cycle,
                    });
                  }
                  const userEmail = readUserEmail();
                  const emailParam = userEmail
                    ? `&customer_email=${encodeURIComponent(userEmail)}`
                    : '';
                  const idParam = txnId
                    ? `&transaction_id=${encodeURIComponent(txnId)}`
                    : '';
                  window.location.href = `${window.location.origin}/success?success=true&plan=${encodeURIComponent(planId)}&billing_cycle=${encodeURIComponent(cycle)}${idParam}${emailParam}`;
                  // ***** Code End BH30072026_1515
                }
              },
            });
            window.__candorPaddleInitialized = true;
            this.paddle = window.Paddle;
          } else if (window.Paddle) {
            this.paddle = window.Paddle;
          } else if (typeof window.Paddle.Setup === 'function') {
            this.paddle = window.Paddle.Setup({
              token: clientKey
            });
          } else {
            this.paddle = window.Paddle;
          }
          // Paddle.js initialized
          // Wait one more time to ensure Checkout is available
          await this.waitForPaddleReady();
          return this.paddle;
        }
      }
    } catch (error) {
      // Failed to initialize Paddle
    }
    return null;
  }

  // Load Paddle.js script
  loadPaddleScript() {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined') {
        reject(new Error('Window is not defined'));
        return;
      }

      if (document.querySelector('script[src*="paddle.js"]')) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://cdn.paddle.com/paddle/v2/paddle.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Paddle.js'));
      document.head.appendChild(script);
    });
  }

  // Get client key from API
  async getClientKeyFromAPI() {
    try {
      const response = await apiService.request({
        method: 'GET',
        url: getConfig('api.endpoints.raw.PAYMENT_PLANS', '/api/v1/payments/plans')
      });
      return response?.paddle_client_key || response?.paddleClientKey;
    } catch (error) {
      // Could not get Paddle client key from API
      return null;
    }
  }

  // Map plan IDs to Paddle price IDs — BH23072026_1330 prefer DB fields, distinct yearly env
  // ***** Code Start BH10092026_1430 - Shared catalog fallback (Dev Server == Prod map)
  mapPlanIdToPriceId(planId, billingInterval = 'monthly', planRow = null) {
    const isYearly = billingInterval === 'yearly' || billingInterval === 'year' || billingInterval === 'annual';
    const planIdStr = String(planId).trim();

    if (planRow && typeof planRow === 'object') {
      const fromDb = isYearly
        ? (planRow.paddle_price_id_yearly || planRow.paddle_yearly_price_id)
        : (planRow.paddle_price_id_monthly || planRow.paddle_price_id || planRow.paddle_monthly_price_id);
      if (fromDb && String(fromDb).startsWith('pri_')) {
        return String(fromDb).trim();
      }
    }

    const env = (typeof process !== 'undefined' && process.env) ? process.env : {};
    let catalogMonthly = {};
    let catalogYearly = {};
    let catalogAddon = {};
    try {
      // eslint-disable-next-line global-require
      const cat = require('../config/paddlePriceCatalog');
      catalogMonthly = cat.PADDLE_PRICE_CATALOG?.monthly || {};
      catalogYearly = cat.PADDLE_PRICE_CATALOG?.yearly || {};
      catalogAddon = cat.PADDLE_PRICE_CATALOG?.addonTokens || {};
    } catch (_) {
      /* catalog optional at runtime */
    }

    const monthlyMap = {
      pro_plan: getConfig('paddle.pricePro') || env.REACT_APP_PADDLE_PRICE_PRO || catalogMonthly.pro_plan || '',
      developer_plus: getConfig('paddle.priceDevPlus') || env.REACT_APP_PADDLE_PRICE_DEVELOPER_PLUS || env.REACT_APP_PADDLE_PRICE_DEV_PLUS || catalogMonthly.developer_plus || '',
      developer_ultimate: getConfig('paddle.priceDevUltimate') || env.REACT_APP_PADDLE_PRICE_DEVELOPER_ULTIMATE || env.REACT_APP_PADDLE_PRICE_DEV_ULTIMATE || catalogMonthly.developer_ultimate || '',
      pay_as_you_go: getConfig('paddle.pricePayAsYouGo') || env.REACT_APP_PADDLE_PRICE_PAY_AS_YOU_GO || catalogMonthly.pay_as_you_go || '',
    };
    const yearlyMap = {
      pro_plan: env.REACT_APP_PADDLE_PRICE_PRO_YEARLY || catalogYearly.pro_plan || '',
      developer_plus: env.REACT_APP_PADDLE_PRICE_DEVELOPER_PLUS_YEARLY || catalogYearly.developer_plus || '',
      developer_ultimate: env.REACT_APP_PADDLE_PRICE_DEVELOPER_ULTIMATE_YEARLY || catalogYearly.developer_ultimate || '',
      pay_as_you_go: env.REACT_APP_PADDLE_PRICE_PAY_AS_YOU_GO_YEARLY || catalogYearly.pay_as_you_go || '',
    };

    if (isYearly) {
      const y = (yearlyMap[planIdStr] || '').trim();
      if (y.startsWith('pri_')) return y;
      // Do not bill the monthly price for a yearly term
      if (planIdStr in yearlyMap) return '';
    } else {
      const m = (monthlyMap[planIdStr] || '').trim();
      if (m.startsWith('pri_')) return m;
    }

    const addon1M = getConfig('paddle.priceAddon1M') || env.REACT_APP_PADDLE_PRICE_ADDON_1M || catalogAddon[1000000] || 'pri_01ka7s22mzpy5s5pxt9v60zdhx';
    const addon2M = getConfig('paddle.priceAddon2M') || env.REACT_APP_PADDLE_PRICE_ADDON_2M || catalogAddon[2000000] || 'pri_01ka7s8x5qg4kdde43y8hrejd7';
    const addon3M = getConfig('paddle.priceAddon3M') || env.REACT_APP_PADDLE_PRICE_ADDON_3M || catalogAddon[3000000] || 'pri_01ka7sba42yb9mcpdhxvk2xxa1';
    // ***** Code End BH10092026_1430
    const addonMap = {
      '4': addon1M,
      '5': addon2M,
      '6': addon3M,
      addon_4: addon1M,
      addon_5: addon2M,
      addon_6: addon3M,
      'add-on-1M': addon1M,
      'add-on-2M': addon2M,
      'add-on-3M': addon3M,
      addon_50k: addon1M,
      addon_100k: addon1M,
      addon_250k: addon2M,
      addon_500k: addon2M,
      'add-on-1m': addon1M,
      'add-on-2m': addon2M,
      'add-on-3m': addon3M,
    };
    if (addonMap[planIdStr]) return addonMap[planIdStr];

    // ***** Code Start BH29072026_1530 - subscription_plans.id is a uuid
    // The maps above are keyed by legacy integer ids, so a uuid plan id never matches.
    // Resolve add-on packs from the DB row instead: token count first, then plan_name.
    if (planRow && typeof planRow === 'object') {
      const rowName = String(planRow.plan_name || planRow.name || '').trim();
      const rowType = String(planRow.plan_type || '').toLowerCase();
      if (rowType === 'addon' || rowType === 'add-on' || /^add-?on/i.test(rowName)) {
        const addonByTokens = {
          1000000: addon1M,
          2000000: addon2M,
          3000000: addon3M,
        };
        const byTokens = addonByTokens[Number(planRow.monthly_limit || planRow.tokens) || 0];
        if (byTokens) return byTokens;
        const byName = addonMap[rowName] || addonMap[rowName.toLowerCase()];
        if (byName) return byName;
      }
    }
    // ***** Code End BH29072026_1530

    return planIdStr.startsWith('pri_') ? planIdStr : planId;
  }

  // ***** Code Start BH30072026_1515 - Overlay fallback when eventCallback does not redirect
  // Paddle dashboard defaults can land on /success?customer_email=... only. Pass an
  // explicit successUrl with success=true + plan/cycle so SuccessPage can verify using
  // the che_/txn_ already stored in sessionStorage. Do not put {TRANSACTION_ID} here —
  // Paddle Billing does not interpolate Stripe-style placeholders.
  buildOverlaySuccessUrl(planId, billingInterval = 'monthly', customerEmail = null) {
    const plan = encodeURIComponent(planId || 'pro_plan');
    const cycle = encodeURIComponent(billingInterval || 'monthly');
    const emailParam = customerEmail
      ? `&customer_email=${encodeURIComponent(customerEmail)}`
      : '';
    return `${window.location.origin}/success?success=true&plan=${plan}&billing_cycle=${cycle}${emailParam}`;
  }
  // ***** Code End BH30072026_1515

  // Create a checkout session for subscription
  async createCheckoutSession(planId, successUrl, cancelUrl, customerEmail = null, planData = null) {
    try {
      // Build request data - backend will fetch plan details from database
      const requestData = {
        plan_id: planId,
        success_url: successUrl,
        cancel_url: cancelUrl
      };

      if (customerEmail) {
        requestData.customer_email = customerEmail;
      }

      // If plan data is provided, include it for validation (backend will verify against database)
      if (planData) {
        requestData.plan_name = planData.name;
        requestData.plan_price = planData.price;
        requestData.plan_monthly_limit = planData.monthly_limit;
        requestData.billing_interval = planData.billing_interval || 'monthly';
      }
      
      const response = await apiService.request({
        method: 'POST',
        url: getConfig('api.endpoints.raw.PAYMENT_CREATE_CHECKOUT', '/api/v1/payments/create-checkout-session'),
        data: requestData
      });

      // Normalize response shape - PRESERVE ALL STATUS FIELDS
      const normalized = {
        transaction_id: response?.transaction_id || response?.transactionId,
        session_id: response?.transaction_id || response?.session_id || response?.sessionId,
        url: response?.checkout_url || response?.url,
        checkout_url: response?.checkout_url || response?.url,
        // CRITICAL: Preserve transaction status fields
        transaction_status: response?.transaction_status || response?.status,
        is_ready: response?.is_ready !== undefined ? response?.is_ready : (response?.transaction_status === 'ready'),
        is_draft: response?.is_draft !== undefined ? response?.is_draft : (response?.transaction_status === 'draft'),
        message: response?.message,
        requires_address: response?.requires_address,
        can_proceed: response?.can_proceed,
        plan_id: response?.plan_id,
        plan_name: response?.plan_name,
        customer_id: response?.customer_id
      };
      // Checkout session created
      return normalized;
    } catch (error) {
      // Failed to create checkout session
      throw error;
    }
  }

  // Redirect to Paddle checkout
  // ***** BH29072026_1530 — planRow lets uuid add-on ids resolve to a Paddle price
  async redirectToCheckout(transactionId, checkoutUrl = null, successUrl = null, cancelUrl = null, planId = null, billingInterval = 'monthly', planRow = null) {
    try {
      // PRIORITY CHECK: If we have a real Paddle transaction ID (starts with txn_),
      // skip ALL URL checks and go straight to Paddle.Checkout.open()
      const isRealPaddleTxn = transactionId && typeof transactionId === 'string' && transactionId.startsWith('txn_');

      if (!isRealPaddleTxn) {
        // No real txn_ — handle non-Paddle / mock flows

        // CLIENT-SIDE PADDLE OVERLAY CHECKOUT FALLBACK:
        // If the user wants Paddle Overlay and we have a valid plan ID (except free_trial),
        // we can dynamically open the native checkout overlay using the price ID!
        if (planId && planId !== 'free_trial') {
          const priceId = this.mapPlanIdToPriceId(planId, billingInterval, planRow);
          if (priceId && typeof priceId === 'string' && priceId.startsWith('pri_')) {
            console.log(`[Paddle] Fallback to client-side inline checkout overlay for price ID: ${priceId}`);
            
            // Initialize Paddle.js if not already initialized
            if (!this.paddle) {
              await this.initializePaddle();
            }
            
            // Ensure Paddle.js is fully ready
            await this.waitForPaddleReady();

            let userEmail = null;
            try {
              const candorUserObj = getAuthUser();
                const candorUser = candorUserObj ? JSON.stringify(candorUserObj) : null;
              if (candorUser) userEmail = JSON.parse(candorUser).email;
            } catch (_) {
              /* ignore */
            }

            // Prefill email when present — needed when server creates a draft txn
            // without customer_id (API key missing customer.read/write).
            window.Paddle.Checkout.open({
              items: [
                {
                  priceId: priceId,
                  quantity: 1
                }
              ],
              ...(userEmail ? { customer: { email: userEmail } } : {}),
              settings: {
                successUrl: this.buildOverlaySuccessUrl(planId, billingInterval, userEmail),
              },
              customData: {
                plan_id: planId,
                billing_cycle: billingInterval,
                ...(userEmail ? { user_email: userEmail } : {}),
              },
            });
            return;
          }
        }

        // ***** Code Start BH29072026_1105 - Mock checkout only in dev / explicit opt-in (Sathis)
        const { isMockPaymentsAllowed } = await import('../config/appConfig');
        const isDevOrMockAllowed = isMockPaymentsAllowed();

        // Mock 2Checkout page (Development / Opt-in testing only)
        if (isDevOrMockAllowed && checkoutUrl && checkoutUrl.includes('/mock-2checkout')) {
          window.location.href = checkoutUrl;
          return;
        }

        // Stripe or 2Checkout direct URL
        if (checkoutUrl && (
          checkoutUrl.includes('stripe.com') ||
          checkoutUrl.includes('2checkout.com')
        )) {
          window.location.href = checkoutUrl;
          return;
        }

        // cs_mock_ or mock_ session IDs — redirect to success URL directly (Development / Opt-in testing only)
        if (isDevOrMockAllowed && transactionId && (String(transactionId).startsWith('cs_mock_') || String(transactionId).startsWith('mock_'))) {
          const finalSuccessUrl = (successUrl || `${window.location.origin}/success?success=true&transaction_id={TRANSACTION_ID}`)
            .replace('{CHECKOUT_SESSION_ID}', transactionId)
            .replace('{TRANSACTION_ID}', transactionId);
          window.location.href = finalSuccessUrl;
          return;
        }

        // Any other non-txn URL with mock=true — redirect to it (Development / Opt-in testing only)
        if (isDevOrMockAllowed && checkoutUrl && checkoutUrl.includes('mock=true')) {
          window.location.href = checkoutUrl;
          return;
        }
        // ***** Code End BH29072026_1105

        // No transaction ID at all
        if (!transactionId) {
          throw new Error('Transaction ID is required to open Paddle checkout');
        }
      }

      // --- REAL PADDLE TRANSACTION: use Paddle.js Checkout.open() ---
      if (!transactionId) {
        throw new Error('Transaction ID is required to open Paddle checkout');
      }

      // Initialize Paddle.js if not already initialized
      if (!this.paddle) {
        await this.initializePaddle();
      }

      // Ensure Paddle.js is fully ready
      await this.waitForPaddleReady();

      // Final format validation
      if (!String(transactionId).startsWith('txn_')) {
        throw new Error(`Invalid transaction ID format. Expected txn_xxxxx, got: ${transactionId}`);
      }

      const cleanTransactionId = String(transactionId).trim();

      let userEmail = null;
      try {
        const candorUserObj = getAuthUser();
                const candorUser = candorUserObj ? JSON.stringify(candorUserObj) : null;
        if (candorUser) userEmail = JSON.parse(candorUser).email;
      } catch (_) {
        /* ignore */
      }

      // Prefill email on draft transactions created without server-side customer_id.
      window.Paddle.Checkout.open({
        transactionId: cleanTransactionId,
        ...(userEmail ? { customer: { email: userEmail } } : {}),
        settings: {
          successUrl: this.buildOverlaySuccessUrl(planId, billingInterval, userEmail),
        },
      });

    } catch (error) {
      // Last resort fallback — show mock checkout via in-app modal by throwing
      // so SubscriptionPlans.js can catch it
      throw error;
    }
  }

  // Get subscription details
  async getSubscriptionDetails(subscriptionId) {
    try {
      const response = await apiService.request({
        method: 'GET',
        url: getConfig('api.endpoints.raw.SUBSCRIPTION_STATUS', '/api/v1/subscriptions/status')
      });
      return response;
    } catch (error) {
      // Failed to get subscription details
      throw error;
    }
  }

  // Cancel subscription
  async cancelSubscription(subscriptionId) {
    try {
      const response = await apiService.request({
        method: 'POST',
        url: getConfig('api.endpoints.raw.SUBSCRIPTION_CANCEL', '/api/v1/payments/cancel-subscription')
      });
      return response;
    } catch (error) {
      // Failed to cancel subscription
      throw error;
    }
  }

  // Update subscription
  async updateSubscription(subscriptionId, newPlanId) {
    try {
      const response = await apiService.request({
        method: 'POST',
        url: getConfig('api.endpoints.raw.SUBSCRIPTION_UPDATE', '/api/v1/subscriptions/update'),
        data: {
          plan_id: newPlanId
        }
      });
      return response;
    } catch (error) {
      // Failed to update subscription
      throw error;
    }
  }
}

const paddleService = new PaddleService();
export default paddleService;

